import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { 
  Heading, SimpleGrid, VStack, Text, Badge, HStack, Icon, Box, 
  Card, CardBody, Stat, StatLabel, StatNumber, StatHelpText,
  Grid, GridItem, Container,
  Table, Thead, Tbody, Tr, Th, Td, TableContainer
} from '@chakra-ui/react';
import { 
  FiUsers, FiAlertTriangle, FiAlertCircle, FiShield, FiBarChart2, FiTarget, FiRepeat, FiCalendar, FiDatabase
} from 'react-icons/fi';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip, Legend, PieChart, Pie, Cell } from 'recharts';
import Page from '../../components/ui/Page';
import SectionCard from '../../components/ui/SectionCard';
import LoadingState from '../../components/ui/LoadingState';
import ErrorState from '../../components/ui/ErrorState';
import useAnalyticsAPI from '../../hooks/useAnalyticsAPI';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

export default function AnalysisImpactPage() {
  const { makeRequest } = useAnalyticsAPI();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchImpactData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await makeRequest('/api/bugs/analytics/client-impact', {});
      console.log('API Response:', result);
      
      if (result.status === 'success' && result.data) {
        setData(result.data);
      } else {
        throw new Error('Failed to load impact data');
      }
    } catch (err) {
      console.error('Failed to fetch impact data:', err);
      setError(err.message || 'Failed to load client impact data');
    } finally {
      setLoading(false);
    }
  }, [makeRequest]);

  useEffect(() => {
    fetchImpactData();
  }, [fetchImpactData]);

  const impactData = data;

  // Process data for charts
  const processedData = useMemo(() => {
    if (!impactData) return {};

    return {
      // Business impact summary
      businessImpact: impactData.businessImpact || {},
      
      // Top affected clients
      topClients: (impactData.clientBreakdown || []).slice(0, 10).map((client, index) => ({
        name: client.clientName || `Client ${index + 1}`,
        issues: client.issueCount || 0,
        severity: client.severityBreakdown || {}
      })),

      // Root cause analysis
      rootCauses: (impactData.rootCauseAnalysis || []).slice(0, 10).map(item => ({
        rootCause: item.rootCause?.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim(),
        module: item.module,
        count: item.count,
        issues: item.issues || [],
        severity: item.severity || {}
      })),

      // Recurring issues
      recurringIssues: (impactData.recurringIssues || []).slice(0, 10).map(item => ({
        name: item.name?.replace(/:/g, ' - ').replace(/_/g, ' '),
        count: item.count
      })),

      // Temporal analysis
      temporalAnalysis: impactData.temporalAnalysis || {},

      // Severity distribution
      severityDistribution: Object.entries(impactData.severityDistribution || {}).map(([severity, count]) => ({
        name: severity.charAt(0).toUpperCase() + severity.slice(1).toLowerCase(),
        value: count
      })).filter(item => item.value > 0)
    };
  }, [impactData]);

  if (loading) return <LoadingState title="Loading Client Impact Analysis" minH="70vh" />;
  if (error) return <ErrorState message={error} />;
  if (!impactData) return <ErrorState message="No impact data available" />;

  const getSeverityColor = (severity) => {
    switch (severity?.toUpperCase()) {
      case 'CRITICAL': return 'red';
      case 'HIGH': return 'orange';
      case 'MEDIUM': return 'yellow';
      case 'LOW': return 'green';
      default: return 'gray';
    }
  };

  return (
    <Page maxW="full">
      <Container maxW="8xl" p={0}>
        {/* Header */}
        <Box mb={8}>
          <Heading size="xl" color="gray.600" mb={2}>Client Impact Analysis</Heading>
          <Text fontSize="lg" color="gray.500">
            Comprehensive analysis of business impact and affected clients
          </Text>
        </Box>

      {/* Business Impact Overview */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mb={6}>
        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Total Clients</StatLabel>
              <StatNumber color="blue.500">{impactData?.summary?.totalClients || 0}</StatNumber>
              <StatHelpText>
                <Icon as={FiUsers} mr={1} />
                Affected clients
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Total Issues</StatLabel>
              <StatNumber color="red.500">{impactData?.summary?.totalIssues || 0}</StatNumber>
              <StatHelpText>
                <Icon as={FiAlertTriangle} mr={1} />
                Total reported issues
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Critical Issues</StatLabel>
              <StatNumber color="red.500">{processedData.businessImpact.criticalIssues || 0}</StatNumber>
              <StatHelpText>
                <Icon as={FiAlertCircle} mr={1} />
                Urgent attention needed
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <StatLabel>High Severity Issues</StatLabel>
              <StatNumber color="orange.500">{processedData.businessImpact.highSeverityIssues || 0}</StatNumber>
              <StatHelpText>
                <Icon as={FiShield} mr={1} />
                Priority issues
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      <Grid templateColumns={{ base: "1fr", lg: "2fr 1fr" }} gap={6}>
        {/* Main Content */}
        <GridItem>
          <VStack spacing={6} align="stretch">
            {/* Severity Distribution */}
            {processedData.severityDistribution.length > 0 && (
              <SectionCard>
                <VStack align="start" spacing={4}>
                  <HStack>
                    <Icon as={FiBarChart2} color="blue.500" />
                    <Heading size="md">Severity Distribution</Heading>
                  </HStack>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={processedData.severityDistribution}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, value }) => `${name}: ${value}`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {processedData.severityDistribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </VStack>
              </SectionCard>
            )}

            {/* Top Affected Clients */}
            {processedData.topClients.length > 0 && (
              <SectionCard>
                <VStack align="start" spacing={4}>
                  <HStack>
                    <Icon as={FiUsers} color="red.500" />
                    <Heading size="md">Top Affected Clients</Heading>
                  </HStack>
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={processedData.topClients} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis 
                        dataKey="name" 
                        tick={{ fontSize: 10, fill: '#4A5568' }}
                        angle={-45}
                        textAnchor="end"
                        height={80}
                      />
                      <YAxis 
                        domain={[0, 'dataMax']}
                        tick={{ fontSize: 12, fill: '#4A5568' }}
                      />
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: '#FFFFFF',
                          border: '1px solid #E2E8F0',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                        }}
                        formatter={(value, name) => [`${value} issues`, 'Issues']}
                      />
                      <Bar dataKey="issues" fill="#E53E3E" name="Issues" />
                      <Legend />
                    </BarChart>
                  </ResponsiveContainer>
                </VStack>
              </SectionCard>
            )}

            {/* Root Cause Analysis */}
            {processedData.rootCauses.length > 0 && (
              <SectionCard>
                <VStack align="start" spacing={4}>
                  <HStack>
                    <Icon as={FiTarget} color="orange.500" />
                    <Heading size="md">Root Cause Analysis</Heading>
                  </HStack>
                  <TableContainer>
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          <Th>Root Cause</Th>
                          <Th>Module</Th>
                          <Th>Count</Th>
                          <Th>Severity</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {processedData.rootCauses.map((item, index) => (
                          <Tr key={index}>
                            <Td>
                              <Text fontWeight="medium">{item.rootCause}</Text>
                            </Td>
                            <Td>
                              <Text fontSize="sm" color="gray.600">{item.module}</Text>
                            </Td>
                            <Td>
                              <Badge colorScheme="blue" variant="solid">
                                {item.count}
                              </Badge>
                            </Td>
                            <Td>
                              <HStack spacing={1}>
                                {Object.entries(item.severity || {}).map(([sev, count]) => (
                                  count > 0 && (
                                    <Badge key={sev} colorScheme={getSeverityColor(sev)} size="sm">
                                      {sev}: {count}
                                    </Badge>
                                  )
                                ))}
                              </HStack>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </VStack>
              </SectionCard>
            )}
          </VStack>
        </GridItem>

        {/* Sidebar */}
        <GridItem>
          <VStack spacing={6} align="stretch">
            {/* Recurring Issues */}
            {processedData.recurringIssues.length > 0 && (
              <SectionCard>
                <VStack align="start" spacing={4}>
                  <HStack>
                    <Icon as={FiRepeat} color="purple.500" />
                    <Heading size="md">Recurring Issues</Heading>
                  </HStack>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={processedData.recurringIssues} layout="horizontal" margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis type="number" domain={[0, 'dataMax']} />
                      <YAxis dataKey="name" type="category" width={150} tick={{ fontSize: 10 }} />
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: '#FFFFFF',
                          border: '1px solid #E2E8F0',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                        }}
                        formatter={(value, name) => [`${value} occurrences`, 'Count']}
                      />
                      <Bar dataKey="count" fill="#805AD5" name="Occurrences" />
                    </BarChart>
                  </ResponsiveContainer>
                </VStack>
              </SectionCard>
            )}

            {/* Temporal Analysis */}
            {processedData.temporalAnalysis && (
              <SectionCard>
                <VStack align="start" spacing={4}>
                  <HStack>
                    <Icon as={FiCalendar} color="teal.500" />
                    <Heading size="md">Temporal Analysis</Heading>
                  </HStack>
                  <VStack align="start" spacing={3}>
                    <Box p={3} bg="teal.50" borderRadius="md" w="full">
                      <Text fontWeight="bold" color="teal.800" mb={1}>
                        Recent Issues
                      </Text>
                      <Text color="teal.700" fontSize="sm">
                        {processedData.temporalAnalysis.recentIssues || 0} issues in the last period
                      </Text>
                    </Box>
                    <Box p={3} bg="purple.50" borderRadius="md" w="full">
                      <Text fontWeight="bold" color="purple.800" mb={1}>
                        Recurring Patterns
                      </Text>
                      <Text color="purple.700" fontSize="sm">
                        {processedData.temporalAnalysis.recurringPatterns || 0} patterns identified
                      </Text>
                    </Box>
                    {processedData.temporalAnalysis.seasonalTrends && processedData.temporalAnalysis.seasonalTrends.length > 0 && (
                      <Box p={3} bg="blue.50" borderRadius="md" w="full">
                        <Text fontWeight="bold" color="blue.800" mb={1}>
                          Seasonal Trends
                        </Text>
                        <Text color="blue.700" fontSize="sm">
                          {processedData.temporalAnalysis.seasonalTrends.length} seasonal patterns detected
                        </Text>
                      </Box>
                    )}
                  </VStack>
                </VStack>
              </SectionCard>
            )}

            {/* Analysis Metadata */}
            {data?.metadata && (
              <SectionCard>
                <VStack align="start" spacing={4}>
                  <HStack>
                    <Icon as={FiDatabase} color="gray.500" />
                    <Heading size="md">Analysis Info</Heading>
                  </HStack>
                  <VStack align="start" spacing={2}>
                    <Text fontSize="sm" color="gray.600">
                      <strong>Generated:</strong> {new Date(data.metadata.generatedAt).toLocaleString()}
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      <strong>Version:</strong> {data.metadata.analysisVersion}
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      <strong>Clients Analyzed:</strong> {data.metadata.totalClientsAnalyzed}
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      <strong>Data Sources:</strong> {data.metadata.dataSources?.join(', ')}
                    </Text>
                  </VStack>
                </VStack>
              </SectionCard>
            )}
          </VStack>
        </GridItem>
      </Grid>
      </Container>
    </Page>
  );
}


